import { YearlessDate, YearlessDateMode } from "../types";

export function validateYearlessDate({
  value,
  constraints,
}: {
  value: YearlessDate | null;
  constraints: {
    required?: boolean;
    unavailable?: {
      dates?: YearlessDate[];
    };
    minDate?: YearlessDate | null;
    maxDate?: YearlessDate | null;
  };
}): boolean {
  const { required, unavailable, minDate, maxDate } = constraints;
  if (!value || (!value?.day && !value?.month)) {
    return required ? false : true;
  }
  if (value.day === null || value.month === null) {
    return false;
  }
  if (
    unavailable?.dates?.some(
      (d) => d.day === value.day && d.month === value.month,
    )
  ) {
    return false;
  }
  const minDateMonth = minDate?.month ?? 1;
  const minDateDay = minDate?.day ?? 1;
  const maxDateMonth = maxDate?.month ?? 12;
  const maxDateDay = maxDate?.day ?? 31;
  if (
    value.month < minDateMonth || // Earlier month
    (value.month === minDateMonth && value.day < minDateDay) // Same month, earlier day
  ) {
    return false;
  }
  if (
    value.month > maxDateMonth || // Later month
    (value.month === maxDateMonth && value.day > maxDateDay) // Same month, later day
  ) {
    return false;
  }
  return true;
}

/**
 * Return list of months
 * 🌍 localeName: name of local,
 *  ✅ monthFormat: short, numeric, long (Default)
 */
export function getMonthNames({
  locale,
  format,
}: {
  locale: string;
  format: Intl.DateTimeFormatOptions["month"];
}): string[] {
  const formatFn = new Intl.DateTimeFormat(locale, { month: format }).format;
  return [...Array(12).keys()].map((m) =>
    formatFn(new Date(Date.UTC(2021, (m + 1) % 12))),
  );
}

/**
 * Given a text value, parse it into a YearlessDate object and validate it.
 * @param value - The text value to parse.
 * @param mode - The mode of the date input.
 * @param removePlaceholder - The function to remove the placeholder from the text value.
 * @returns An object containing the parsed YearlessDate object, the input validity, and the input emptiness.
 */
export function parseInputValue(
  value: string,
  mode: YearlessDateMode,
  removePlaceholder: (value: string) => string,
): {
  value: YearlessDate | null;
  isInputValid: boolean;
  isInputEmpty: boolean;
} {
  const valueMinusPlaceholder = removePlaceholder(value);
  const [segment1, segment2] = valueMinusPlaceholder.split("/");
  const incompleteFirstSegment = segment1?.length !== 2;
  const incompleteSecondSegment = segment2?.length !== 2;
  if (mode === "dd/mm") {
    return {
      value: {
        day: incompleteFirstSegment ? null : parseInt(segment1),
        month: incompleteSecondSegment ? null : parseInt(segment2),
      },
      isInputValid: !incompleteFirstSegment && !incompleteSecondSegment,
      isInputEmpty: valueMinusPlaceholder === "",
    };
  }
  return {
    value: {
      day: incompleteSecondSegment ? null : parseInt(segment2),
      month: incompleteFirstSegment ? null : parseInt(segment1),
    },
    isInputValid: !incompleteFirstSegment && !incompleteSecondSegment,
    isInputEmpty: valueMinusPlaceholder === "",
  };
}

/**
 * Swap the mode of a date input.
 * @param inputString - The text value to swap the mode of.
 * @param previousMode - The previous mode of the date input.
 * @param mode - The new mode of the date input.
 * @returns The text value with the new mode.
 */
export function swapMode(
  inputString: string,
  previousMode: YearlessDateMode,
  mode: YearlessDateMode,
) {
  const { day, month } = divideSegments(inputString, previousMode);
  return stringifyYearlessDate(day, month, mode);
}

/**
 * Given a text value and a mode, divide the segments of a date input.
 * @param value - The text value to divide the segments of.
 * @param mode - The mode of the date input.
 * @returns An object containing the day and month segments.
 */
function divideSegments(
  value: string,
  mode: YearlessDateMode,
): {
  day: string;
  month: string;
} {
  const [segment1, segment2] = value.split("/");
  if (mode === "dd/mm") {
    return { day: segment1, month: segment2 };
  }
  return { day: segment2, month: segment1 };
}

/**
 * Convert a YearlessDate object into a text value.
 * @param day - The day of the date.
 * @param month - The month of the date.
 * @param mode - The mode of the date input.
 * @returns The stringified date.
 */
export function stringifyYearlessDate(
  day: number | string | null,
  month: number | string | null,
  mode: YearlessDateMode,
) {
  const dd = (day ?? "__").toString().padStart(2, "0");
  const mm = (month ?? "__").toString().padStart(2, "0");
  if (mode === "dd/mm") {
    return `${dd}/${mm}`;
  }
  return `${mm}/${dd}`;
}

/**
 * Get the number of days in a given month.
 * @param month - The month number (1-12)
 * @returns The number of days in the month
 */
export function getDaysInMonth(month: number): number {
  // Use a leap year (2024) to account for February having 29 days
  return new Date(2024, month, 0).getDate();
}

/**
 * Get months that don't have enough days for the given day.
 * @param day - The day number (1-31)
 * @returns Array of month numbers that don't have enough days for the given day
 */
export function getInvalidMonthsForDay(day: number): number[] {
  const invalidMonths: number[] = [];

  for (let month = 1; month <= 12; month++) {
    const daysInMonth = getDaysInMonth(month);
    if (day > daysInMonth) {
      invalidMonths.push(month);
    }
  }

  return invalidMonths;
}
